import { ProjectUpdates } from '@/components/ProjectRevisions';
import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Signin from "@/pages/Signin";
import { Forgot, Reset, Verify } from "@/pages/AuthFlows";
import { Dashboard } from "@/pages/Dashboard";
import { Account, Security } from "@/pages/Account";
import { AdminConsole } from "@/pages/Admin";
import ContractorProfile from "@/pages/ContractorProfile";
import ContractorQualification from "@/pages/ContractorQualification";
import Availability from "@/pages/Availability";
import { PublicContractorProfile } from "@/pages/Marketplace";
import MarketplaceSearch from "@/pages/MarketplaceSearch";
import { CompanyJobRequestDetail } from "@/pages/CompanyJobRequests";
import FindContractors from "@/pages/FindContractors";
import FindCrew, { ContractorSearchRoute } from "@/pages/FindCrew";
import { JobsList, NewJob, JobDetail } from "@/pages/Jobs";
import { LeadBookingsInbox, LeadBookingDetail, BookLeadWizard } from "@/pages/Bookings";
import { LeadCrewManagement, HelperInbox, HelperInvitationDetail } from "@/pages/Crews";
import { NotifInbox, NotifPrefs } from "@/pages/Notifications";
import { DisputesInbox, DisputeDetail, CounterpartyInbox, CounterpartyDetail } from "@/pages/Disputes";
import { AdminDisputesInbox, AdminDisputeDetail, AdminReachability } from "@/pages/AdminDisputes";
import { RFQInbox, RFQDetail } from "@/pages/RFQ";
import SupportSnapshot from "@/pages/SupportSnapshot";
import HandoffCallback from "@/pages/HandoffCallback";
import HelpCenter, { HelpContact } from "@/pages/Help";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/signin" replace />;
  if (role && !user.roles?.includes(role)) return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/contact" element={<HelpContact />} />
          <Route path="/help/:guideId" element={<HelpCenter />} />
          <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
          <Route path="/signin" element={<PublicOnly><Signin /></PublicOnly>} />
          <Route path="/auth/handoff" element={<HandoffCallback />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/reset" element={<Reset />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/app/account" element={<Protected><Account /></Protected>} />
          <Route path="/app/contractor-profile" element={<Protected><ContractorProfile /></Protected>} />
          <Route path="/app/contractor-profile/qualification" element={<Protected><ContractorQualification /></Protected>} />
          <Route path="/app/contractor-profile/availability" element={<Protected><Availability /></Protected>} />
          <Route path="/app/find-crew" element={<Protected><FindCrew /></Protected>} />
          <Route path="/app/find-contractors" element={<Protected><ContractorSearchRoute><FindContractors /></ContractorSearchRoute></Protected>} />
          <Route path="/app/marketplace/leads" element={<Navigate to="/app/find-contractors?view=leads" replace />} />
          <Route path="/app/marketplace/search" element={<Protected><ContractorSearchRoute><MarketplaceSearch /></ContractorSearchRoute></Protected>} />
          <Route path="/app/marketplace/crew-shape" element={<Navigate to="/app/find-contractors" replace />} />
          <Route path="/app/marketplace/integrator" element={<Navigate to="/app/find-contractors?marketplace=integrator" replace />} />
          <Route path="/app/marketplace/workforce" element={<Navigate to="/app/find-contractors?marketplace=workforce" replace />} />
          <Route path="/app/marketplace/event-tech" element={<Navigate to="/app/find-contractors?marketplace=event_tech" replace />} />
          <Route path="/app/marketplace/helpers" element={<Protected><Navigate to="/app/find-crew" replace /></Protected>} />
          <Route path="/app/contractors/:uuid" element={<Protected><PublicContractorProfile /></Protected>} />
          <Route path="/app/jobs" element={<Protected role="client"><JobsList /></Protected>} />
          <Route path="/app/jobs/new" element={<Protected role="client"><NewJob /></Protected>} />
          <Route path="/app/project-updates/:id" element={<Protected><ProjectUpdates /></Protected>} />
          <Route path="/app/jobs/:id" element={<Protected><JobDetail /></Protected>} />
          <Route path="/app/jobs/:job_id/book/:lead_uuid" element={<Protected role="client"><BookLeadWizard /></Protected>} />
          <Route path="/app/contractors/:uuid/book" element={<Protected role="client"><BookLeadWizard /></Protected>} />
          <Route path="/app/leads/me/bookings" element={<Protected><LeadBookingsInbox /></Protected>} />
          <Route path="/app/company-requests/:id" element={<Protected><CompanyJobRequestDetail /></Protected>} />
          <Route path="/app/companies/:company_id/request-job" element={<Protected role="client"><BookLeadWizard /></Protected>} />
          <Route path="/app/leads/me/bookings/:id" element={<Protected role="lead_contractor"><LeadBookingDetail /></Protected>} />
          <Route path="/app/leads/me/bookings/:id/crew" element={<Protected><LeadCrewManagement /></Protected>} />
          <Route path="/app/helpers/me/invitations" element={<Protected><HelperInbox /></Protected>} />
          <Route path="/app/helpers/me/invitations/:id" element={<Protected><HelperInvitationDetail /></Protected>} />
          <Route path="/app/security" element={<Protected><Security /></Protected>} />
          <Route path="/app/notifications" element={<Protected><NotifInbox /></Protected>} />
          <Route path="/app/notifications/preferences" element={<Protected><NotifPrefs /></Protected>} />
          <Route path="/app/rfq" element={<Protected><RFQInbox /></Protected>} />
          <Route path="/app/rfq/:id" element={<Protected><RFQDetail /></Protected>} />
          <Route path="/app/disputes" element={<Protected><DisputesInbox /></Protected>} />
          <Route path="/app/disputes/:id" element={<Protected><DisputeDetail /></Protected>} />
          <Route path="/app/counterparty/disputes" element={<Protected><CounterpartyInbox /></Protected>} />
          <Route path="/app/counterparty/disputes/:id" element={<Protected><CounterpartyDetail /></Protected>} />
          <Route path="/app/admin/disputes" element={<Protected role="super_admin"><AdminDisputesInbox /></Protected>} />
          <Route path="/app/admin/disputes/:id" element={<Protected role="super_admin"><AdminDisputeDetail /></Protected>} />
          <Route path="/app/admin/reachability" element={<Protected role="super_admin"><AdminReachability /></Protected>} />
          <Route path="/app/admin/users/:uuid/snapshot" element={<Protected role="super_admin"><SupportSnapshot /></Protected>} />
          <Route path="/app/admin" element={<Protected role="super_admin"><AdminConsole /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
